import { useState } from "react";
import { Share2, Loader2 } from "lucide-react";
import { kitFor, BODY, LEFT_SLEEVE, RIGHT_SLEEVE } from "./fpl/ShirtKit";
import { armbandsFor, splitPoints, starOf, pkey } from "./fpl/fplUtils";

const W = 1080;
const H = 1920;

function fitText(ctx, text, maxWidth) {
  let t = String(text ?? "");
  if (ctx.measureText(t).width <= maxWidth) return t;
  while (t.length > 1 && ctx.measureText(t + "…").width > maxWidth) t = t.slice(0, -1);
  return t + "…";
}


function drawShirt(ctx, cx, top, size, player, isGK) {
  const kit = kitFor(player.short, player.color, isGK);
  const body = new Path2D(BODY);
  ctx.save();
  ctx.translate(cx - size / 2, top);
  ctx.scale(size / 64, size / 64);
  ctx.save();
  ctx.clip(body);
  ctx.fillStyle = kit.body;
  ctx.fillRect(0, 0, 64, 64);
  ctx.fillStyle = kit.alt;
  if (kit.pattern === "stripes") [22, 30, 38].forEach((sx) => ctx.fillRect(sx, 0, 4, 64));
  if (kit.pattern === "halves") ctx.fillRect(32, 0, 32, 64);
  ctx.fillStyle = kit.sleeve;
  ctx.fill(new Path2D(LEFT_SLEEVE));
  ctx.fill(new Path2D(RIGHT_SLEEVE));
  ctx.restore();
  ctx.fillStyle = kit.trim;
  ctx.fill(new Path2D("M26 3.5 Q32 9 38 3.5 L36 3 Q32 7 28 3 Z"));
  ctx.strokeStyle = "rgba(0,0,0,0.4)";
  ctx.lineWidth = 1;
  ctx.stroke(body);
  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r, fill) {
  ctx.beginPath();
  ctx.roundRect ? ctx.roundRect(x, y, w, h, r) : ctx.rect(x, y, w, h);
  ctx.fillStyle = fill;
  ctx.fill();
}

function drawPitch(ctx, x, y, w, h, side) {
  const xi = side.xi || [];
  const points = splitPoints(xi, side.points);
  const armbands = armbandsFor(xi);
  const star = starOf(points);
  ctx.textAlign = "center";
  ctx.font = "800 30px Inter, Arial, sans-serif";
  ctx.fillStyle = "#10B981";
  ctx.fillText("STARTING XI", x + w / 2, y - 22);
  ctx.save();
  ctx.beginPath();
  ctx.roundRect ? ctx.roundRect(x, y, w, h, 18) : ctx.rect(x, y, w, h);
  ctx.clip();
  for (let b = 0; b < 11; b++) {
    ctx.fillStyle = b % 2 ? "#09893f" : "#0b9a4c";
    ctx.fillRect(x, y + (h * b) / 11, w, h / 11 + 1);
  }
  ctx.strokeStyle = "rgba(255,255,255,0.55)";
  ctx.lineWidth = 3;
  ctx.strokeRect(x + w * 0.04, y + h * 0.03, w * 0.92, h * 0.95);
  ctx.beginPath();
  ctx.arc(x + w / 2, y + h * 0.03, w * 0.13, 0, Math.PI);
  ctx.stroke();
  ctx.strokeRect(x + w * 0.21, y + h * 0.81, w * 0.58, h * 0.17);
  ctx.strokeRect(x + w * 0.36, y + h * 0.91, w * 0.28, h * 0.07);
  ctx.restore();

  xi.forEach((s) => {
    if (!s.player) return;
    const k = pkey(s.player);
    const cx = x + (w * s.x) / 100;
    const cy = y + (h * (s.y - 3)) / 100;
    const isStar = star === k;
    drawShirt(ctx, cx, cy - 58, 64, s.player, s.cat === "GK");
    if (armbands[k]) {
      ctx.beginPath(); ctx.arc(cx - 30, cy - 52, 13, 0, Math.PI * 2);
      ctx.fillStyle = "#1a1a1a"; ctx.fill();
      ctx.strokeStyle = "#fff"; ctx.lineWidth = 2; ctx.stroke();
      ctx.fillStyle = "#fff"; ctx.font = "800 16px Inter, Arial, sans-serif";
      ctx.fillText(armbands[k], cx - 30, cy - 46);
    }
    if (isStar) {
      ctx.beginPath(); ctx.arc(cx + 30, cy - 52, 13, 0, Math.PI * 2);
      ctx.fillStyle = "#FFD700"; ctx.fill();
      ctx.fillStyle = "#0F172A"; ctx.font = "800 18px Inter, Arial, sans-serif";
      ctx.fillText("\u2605", cx + 30, cy - 46);
    }
    const tw = 108;
    roundRect(ctx, cx - tw / 2, cy + 8, tw, 26, 4, isStar ? "#FFD700" : "#ffffff");
    ctx.fillStyle = "#0F172A";
    ctx.font = "700 18px Inter, Arial, sans-serif";
    const last = s.player.name.split(" ").slice(1).join(" ") || s.player.name;
    ctx.fillText(fitText(ctx, last, tw - 8), cx, cy + 27);
    roundRect(ctx, cx - tw / 2, cy + 34, tw, 24, 4, "#0F172A");
    ctx.fillStyle = isStar ? "#FFD700" : "#10B981";
    ctx.font = "700 17px Inter, Arial, sans-serif";
    ctx.fillText(`${points[k] ?? 0} pts`, cx, cy + 52);
  });

  const sp = xi.find((s) => s.player && pkey(s.player) === star)?.player;
  if (sp) {
    roundRect(ctx, x, y + h + 24, w, 70, 14, "#FFD700");
    ctx.fillStyle = "#0F172A";
    ctx.font = "800 18px Inter, Arial, sans-serif";
    ctx.fillText("\u2605 PLAYER OF THE SEASON", x + w / 2, y + h + 52);
    ctx.font = "700 22px Inter, Arial, sans-serif";
    ctx.fillText(fitText(ctx, `${sp.name} \u00b7 ${points[star]} pts`, w - 30), x + w / 2, y + h + 80);
  }
}

function drawCard(me, them) {
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background
  ctx.fillStyle = "#0F172A";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, -120, 60, W / 2, 300, 900);
  glow.addColorStop(0, "rgba(16, 185, 129,0.28)");
  glow.addColorStop(0.5, "rgba(30, 41, 59,0.16)");
  glow.addColorStop(1, "rgba(15, 23, 42,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, 760);

  const center = W / 2;
  const meWon = me.points > them.points;
  const tie = me.points === them.points;

  // Wordmark
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.font = "800 62px Inter, Arial, sans-serif";
  ctx.fillStyle = "#ffffff";
  const fw = ctx.measureText("FOOTIE ").width;
  const cw = ctx.measureText("CLASH").width;
  const x0 = center - (fw + cw) / 2;
  ctx.fillText("FOOTIE ", x0 + fw / 2, 150);
  ctx.fillStyle = "#38BDF8";
  ctx.fillText("CLASH", x0 + fw + cw / 2, 150);

  // Eyebrow
  ctx.font = "700 30px Inter, Arial, sans-serif";
  ctx.fillStyle = "#10B981";
  ctx.fillText("F U L L   T I M E", center, 230);

  // Headline
  ctx.font = "800 68px Inter, Arial, sans-serif";
  ctx.fillStyle = "#ffffff";
  const headline = tie ? "IT'S A DRAW" : `${(meWon ? me.name : them.name).toUpperCase()} WINS`;
  ctx.fillText(fitText(ctx, headline, W - 120), center, 320);

  // Scoreline
  const colX = [W * 0.27, W * 0.73];
  const sides = [me, them];
  ctx.font = "700 40px Inter, Arial, sans-serif";
  sides.forEach((s, i) => {
    const won = tie ? false : i === 0 ? meWon : !meWon;
    ctx.fillStyle = "#ffffff";
    ctx.fillText(fitText(ctx, s.name, 420), colX[i], 440);
    ctx.font = "800 150px Inter, Arial, sans-serif";
    ctx.fillStyle = won ? "#10B981" : "#d9c6e6";
    ctx.fillText(String(s.points), colX[i], 600);
    ctx.font = "600 30px Inter, Arial, sans-serif";
    ctx.fillStyle = "#94A3B8";
    ctx.fillText(`${s.w}W  ${s.d}D  ${s.l}L`, colX[i], 650);
    ctx.font = "700 40px Inter, Arial, sans-serif";
  });
  ctx.font = "800 60px Inter, Arial, sans-serif";
  ctx.fillStyle = "#94A3B8";
  ctx.fillText("–", center, 580);
  ctx.font = "600 26px Inter, Arial, sans-serif";
  ctx.fillStyle = "#94A3B8";
  ctx.fillText("points after a 38-game season", center, 710);

  // FPL pitches with shirts + points
  sides.forEach((s, i) => {
    const x = i === 0 ? 40 : W / 2 + 20;
    drawPitch(ctx, x, 800, W / 2 - 60, 720, s);
  });

  // Footer
  ctx.textAlign = "center";
  ctx.fillStyle = "#38BDF8";
  ctx.font = "700 32px Inter, Arial, sans-serif";
  let origin = "";
  try { origin = window.location.host; } catch (e) { origin = ""; }
  ctx.fillText(`Draft your XI at ${origin}`, center, H - 110);
  ctx.fillStyle = "#8a7499";
  ctx.font = "500 24px Inter, Arial, sans-serif";
  ctx.fillText("Demo mode — for entertainment purposes only", center, H - 64);

  return canvas;
}

const ShareResultButton = ({ me, them, className = "" }) => {
  const [busy, setBusy] = useState(false);

  const onShare = async () => {
    setBusy(true);
    try {
      const canvas = drawCard(me, them);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "footie-clash-result.png", { type: "image/png" });
      const shareText = `${me.name} ${me.points}–${them.points} ${them.name} · Footie Clash`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Footie Clash", text: shareText });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "footie-clash-result.png";
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      /* user cancelled share or unsupported — no-op */
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      data-testid="share-result-btn"
      onClick={onShare}
      disabled={busy}
      className={`btn-ghost w-full rounded-xl py-3 font-medium text-sm text-white flex items-center justify-center gap-2 disabled:opacity-50 ${className}`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
      Share result
    </button>
  );
};

export default ShareResultButton;
